// src/components/parent/ParentInsightsView.tsx
// Realistic teacher-to-parent insights dashboard

import React, { useState, useEffect } from 'react'
import { Clock, TrendingUp, AlertCircle, CheckCircle, Brain, Target, Users } from 'lucide-react'
import { analytics, type ChildProgress } from '../../services/analytics'
import { narrativeGenerator } from '../../services/parentNarrativeGenerator'

interface ParentInsightsViewProps {
  childId: string
  childName: string
}

export default function ParentInsightsView({ childId, childName }: ParentInsightsViewProps) {
  const [progress, setProgress] = useState<ChildProgress | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadProgress()
  }, [childId])

  const loadProgress = async () => {
    setLoading(true)
    const data = await analytics.getProgress()
    setProgress(data)
    setLoading(false)
  }

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          <p className="ml-3 text-gray-600">Loading insights for {childName}...</p>
        </div>
      </div>
    )
  }

  const narrative = progress && progress.totalMinutes > 0 ? narrativeGenerator.generate(progress, childName) : []
  const avgSessionTime = progress && progress.totalSessions > 0 ? Math.round(progress.totalMinutes / progress.totalSessions) : 0

  if (!progress || progress.totalMinutes === 0) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-white rounded-lg border p-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            {childName}'s Learning Insights
          </h2>
          <p className="text-gray-600 mb-4">
            {childName} hasn't completed any learning sessions yet, so there's nothing 
            to observe yet. After {childName} finishes their first 3-4 activities (usually 
            takes 2-3 days of using the platform), you'll start seeing patterns here.
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded p-4">
            <p className="text-blue-800 text-sm">
              <strong>What you'll see soon:</strong> How long {childName} typically focuses, 
              which times of day work best, and what types of activities they gravitate toward. 
              Think of this like the first few weeks of school - I'm getting to know your child.
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      
      {/* Header with key stats */}
      <div className="bg-white rounded-lg border p-6">
        <h1 className="text-2xl font-semibold text-gray-900 mb-4">
          Learning Check-in: {childName}
        </h1>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center">
                <CheckCircle className="w-10 h-10 text-white" />
              </div>
              <div>
                <div className="text-2xl font-bold text-blue-900">{progress.totalSessions || 0}</div>
                <div className="text-sm text-blue-700">Sessions completed</div>
              </div>
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                <Clock className="w-10 h-10 text-white" />
              </div>
              <div>
                <div className="text-2xl font-bold text-green-900">{avgSessionTime} min</div>
                <div className="text-sm text-green-700">Average session</div>
              </div>
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-white" />
              </div>
              <div>
                <div className="text-2xl font-bold text-orange-900">{progress.currentStreak || 0}</div>
                <div className="text-sm text-orange-700">Day streak</div>
              </div>
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-500 rounded-full flex items-center justify-center">
                <Brain className="w-5 h-5 text-white" />
              </div>
              <div>
                <div className="text-2xl font-bold text-purple-900">{progress.weekSessions || 0}</div>
                <div className="text-sm text-purple-700">This week</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Teacher Narrative - Lead with this */}
      <div className="bg-white rounded-lg border p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          How {childName} is Doing
        </h2>
        
        <div className="space-y-4">
          {narrative.map((para, index) => (
            <p key={index} className="text-gray-700 leading-relaxed">
              {para.text}
            </p>
          ))}
        </div>
      </div>
    
      {/* ADD IEP SECTION  */}
      {progress.totalSessions > 5 && (
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
          <h3 className="font-semibold text-purple-900 mb-2 flex items-center gap-2">
            <Users className="w-5 h-5" />
            For Sharing with Teachers/Therapists
          </h3>
          <p className="text-sm text-purple-800 mb-3">
            Copy this if you need to update {childName}'s support team:
          </p>
          <div className="bg-white rounded p-3 text-sm text-gray-700 border border-purple-100">
            "{childName} has completed {progress.totalSessions} independent literacy sessions 
            over the past few weeks, averaging {avgSessionTime} minutes of focused work. 
            {progress.strengths?.[0] && `They're showing strength in ${progress.strengths[0].replace(/_/g, ' ')}.`}
            {progress.practicingAreas?.[0] && ` Currently working on ${progress.practicingAreas[0].replace(/_/g, ' ')}.`}"
          </div>
          <button 
            onClick={() => {
              const text = `${childName} has completed ${progress.totalSessions} independent literacy sessions over the past few weeks, averaging ${avgSessionTime} minutes of focused work. ${progress.strengths?.[0] ? `They're showing strength in ${progress.strengths[0].replace(/_/g, ' ')}.` : ''} ${progress.practicingAreas?.[0] ? `Currently working on ${progress.practicingAreas[0].replace(/_/g, ' ')}.` : ''}`
              navigator.clipboard.writeText(text)
            }}
            className="mt-2 text-xs text-purple-600 hover:text-purple-700 underline"
          >
            📋 Copy to clipboard
          </button>
        </div>
      )}

      {/* Current focus areas */}
      <div className="bg-white rounded-lg border p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          Current Learning Focus
        </h2>
        
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h3 className="font-medium text-gray-900 mb-3">Strong areas we're building on:</h3>
            <div className="space-y-2">
              {progress.strengths && progress.strengths.length > 0 ? (
                progress.strengths.slice(0, 3).map((strength) => (
                  <div key={strength} className="text-sm bg-green-50 rounded px-3 py-2 text-green-800">
                    {strength.replace(/_/g, ' ')}
                  </div>
                ))
              ) : (
                <p className="text-sm text-gray-500 italic">Still gathering data...</p>
              )}
            </div>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-900 mb-3">Areas we're working on:</h3>
            <div className="space-y-2">
              {progress.practicingAreas && progress.practicingAreas.length > 0 ? (
                progress.practicingAreas.slice(0, 3).map((area) => (
                  <div key={area} className="text-sm bg-blue-50 rounded px-3 py-2 text-blue-800">
                    {area.replace(/_/g, ' ')}
                  </div>
                ))
              ) : (
                <p className="text-sm text-gray-500 italic">Exploring different activities...</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Practical parent section */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h2 className="text-lg font-semibold text-blue-900 mb-4">
          What This Means at Home
        </h2>
        
        <div className="space-y-3 text-sm">
          {avgSessionTime > 15 && (
            <p className="text-blue-800">
              <strong>Good focus stamina:</strong> They can probably handle 10-15 minute homework chunks without getting overwhelmed.
            </p>
          )}
          
          {progress.currentStreak >= 3 && (
            <p className="text-blue-800">
              <strong>Building routine:</strong> They seem to be in a good learning rhythm. Try to keep screen time and activity schedules consistent.
            </p>
          )}
          
          {progress.practicingAreas && progress.practicingAreas.length > 0 && (
            <p className="text-blue-800">
              <strong>Growing edges:</strong> If they get frustrated with {progress.practicingAreas[0].replace(/_/g, ' ')} activities at home, that's normal - it means they're working at the right challenge level.
            </p>
          )}

          <p className="text-blue-800">
            <strong>Need more context?</strong> These insights are based on learning activity patterns. If you have specific concerns about {childName}'s learning, consider discussing them with their teacher or learning specialist.
          </p>
        </div>
      </div>

    </div>
  )
}